import './styles.scss';
import React, {Dispatch, useCallback, useEffect, useLayoutEffect, useState} from "react";

/* types, constants, utils */
import * as Constants from "../../constants";
import Utils from "../../utils";
import {CardContext, IFileAttributes} from '../../types/document';
import DocumentUtils from "../../utils/document-utils";

/* bootstrap */
import {Button, Col, Container, Row} from "react-bootstrap";

/* redux */
import {useDispatch, useSelector} from "react-redux";
import {addCrumb} from "../../redux/slices/breadcrumb-slice";
import {AnyAction} from "@reduxjs/toolkit";
import {
  setContentView, setInitialRecord,
  setPanelCollapsed, setPublicRecord,
  setRecordTool,
  setViewerFullscreen, setViewerRecord, viewStateReset
} from "../../redux/slices/viewer-slice";
import {RootState} from "../../redux";
import {IViewerState} from "../../types/redux/viewer";

/* api */
import SearchService from "../../api/search";
import {useQuery} from "react-query";

/* components */
import ViewerToolbar from "../../components/viewer/viewer-toolbar";
import ViewerPanel from "../../components/viewer/viewer-panel";
import ViewerTools from "../../components/viewer/viewer-tools";
import ViewScrollContainer from "../../components/view/view-scroll-container";

import SavedService from "../../api/saved";
import {setSavedDocuments} from "../../redux/slices/saved-slice";
import {ISavedState, TSavedDocument} from "../../types/redux/saved";
import {IUserState} from "../../types/redux/user";
import { PageContext } from '../../types/app';
import BackSearch from "../../components/viewer/viewer-toolbar/back-search";

import ViewerMobile from "./mobile";
import ViewerDocument from "../../components/viewer/viewer-document";
import BackMenu from "../../components/viewer/viewer-toolbar/back-menu";
import IndustryNavbar from "../../components/industry-navbar";
import usePlatform from "../../hooks/usePlatform";

const Viewer = ():JSX.Element => {
  /* redux */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const viewerState: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer);
  const savedState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);
  const userState: IUserState = useSelector<RootState, IUserState>(state => state.user);

  /* state */
  const [initialRecordId, setInitialRecordId] = useState<string | null>(null);

  /* state */
  const activeRecordTool: string = useSelector<RootState, IViewerState>(state => state.viewer).recordTool;
  const [fileAttributes, setFileAttributes] = useState<IFileAttributes | null>(null);

  /* hooks*/
  const platform = usePlatform();


  /* Initial record */
  const {
    isSuccess: isSuccessInitialRecord,
    data: initialRecordData,
    refetch: initialRecordFetch
  } = useQuery<any, Error>(
    [Constants.RECORD + '_id:' + initialRecordId],
    async ({signal}) => {
      return await SearchService.solrSingleDocumentQuery(initialRecordId, signal);
    },
    {
      enabled: false
    }
  );

  useEffect(()=>{
    const exitFullscreen =  (e: any):void=>{
      if (e.code === Constants.ESCAPE) {
        e.preventDefault();
        dispatch(setPanelCollapsed(false))
        dispatch(setViewerFullscreen(false))
      }
    }
    window.addEventListener(Constants.KEYDOWN, event => exitFullscreen(event), true);
    return ()=> {
      dispatch(viewStateReset())
      window.removeEventListener(Constants.RESIZE, event => exitFullscreen(event), true);
    }
  }, [dispatch])

  useLayoutEffect(()=>{
    const searchParams: URLSearchParams = new URLSearchParams(window.location.search);

    let defaultRecordTool: string = Constants.METADATA;
    defaultRecordTool = searchParams.get(Constants.RECORD_TOOL_PARAM) ? searchParams.get(Constants.RECORD_TOOL_PARAM) : defaultRecordTool;
    handleToolSelect(defaultRecordTool);

    setInitialRecordId(searchParams.get(Constants.INITIAL_ID));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(()=>{
    if (!initialRecordId) return;

    initialRecordFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecordId]);

  /* Viewer record */
  const {
    isSuccess: isSuccessRecord,
    data: viewerRecordData,
    refetch: viewRecordRefetch
  } = useQuery<any, Error>(
    [Constants.RECORD + '_id:' + viewerState.selectedRecord?.id],
    async ({signal}) => {
      const response: any = await SearchService.solrSingleDocumentQuery(viewerState.selectedRecord.id, signal);

      /* check if restricted and if so, see retrieve public if available */
      const record: any = response.response.docs[0];
      const type: string = DocumentUtils.getFileAttributes(record).type;
      if ((type === Constants.CONFIDENTIAL) ||
        (type === Constants.COPYRIGHT) ||
        (type === Constants.CONFIDENTIAL)) {
        const publicRecord: any = await SearchService.solrPublicQuery(record.id, record.bn);
        dispatch(setPublicRecord(publicRecord.response.docs[0]));
      } else {
        dispatch(setPublicRecord(null));
      }

      /* return current record */
      return response;
    },
    {
      enabled: false
    }
  );

  useEffect(()=>{
    /* Address the case where a tool in mobile selected that is not available in Desktop */
    const checkForToolChange = ()=>{
      /* todo: jain - handle tool select on resizing */
     /* const isNowDesktop: boolean = platform.isDesktop;
      if ((!isDesktop && isNowDesktop) && ((activeRecordTool === Constants.DOCUMENT) || (activeRecordTool === null))) {
        handleToolSelect(Constants.METADATA)
      }
      setIsDesktop(isNowDesktop);*/
    }
    window.addEventListener(Constants.RESIZE, (event) => checkForToolChange());

    return ()=>{
      dispatch(setViewerFullscreen(false))
      window.removeEventListener(Constants.RESIZE, (event) => checkForToolChange());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ activeRecordTool, platform.isDesktop])

  useEffect(()=>{
    if (!viewerState.initialRecord) return;

    const savedDocumentData: TSavedDocument | undefined = savedState.documents.find(savedDocument => savedDocument.documentId === viewerState.initialRecord.id);
    if (!savedDocumentData && userState.loggedIn) {
      SavedService.getSavedDocumentsViaIds([viewerState.initialRecord.id]).then((response:any) => {
        dispatch(setSavedDocuments(response.savedDocument));
      }).catch(e => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.initialRecord, userState.loggedIn]);

  useEffect(()=> {
    if (!viewerState.selectedRecord) return;

    /* Add to breadcrumb */
    dispatch(addCrumb({
      label: Utils.toTitleCase(viewerState.selectedRecord.ti),
      href: '/' + Constants.VIEWER,
      level: 3,
      active: true
    }));

    setFileAttributes(DocumentUtils.getFileAttributes(viewerState.selectedRecord))

    viewRecordRefetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.selectedRecord])

  useEffect(()=>{
    if (isSuccessInitialRecord && initialRecordData) {
      /* More Like This, Bates and Browse based on selection outside viewer */
      if (initialRecordData.response.docs.length > 0) {
        const documentResponse: any = {...initialRecordData.response.docs[0]};
        if (initialRecordData.moreLikeThis) {
          documentResponse.moreLikeThis = initialRecordData.moreLikeThis;
        }

        dispatch(setInitialRecord(documentResponse));
        dispatch(setViewerRecord(documentResponse));
        populateView(initialRecordData)
        /*dispatch(setSelectedRecord(documentResponse));*/
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecordData, isSuccessInitialRecord])

  const populateView = (viewerRecordData:any): void =>{
    if (viewerRecordData.response.docs.length > 0) {
      dispatch(setContentView(Constants.PDF));
      dispatch(setPanelCollapsed(false));

      const record: any = viewerRecordData.response.docs[0];
      setFileAttributes(DocumentUtils.getFileAttributes(record))
      dispatch(setViewerRecord(record))
    } else {
      handleToolSelect(Constants.RECORD_NOT_FOUND)
      dispatch(setPanelCollapsed(true))
      setViewerRecord(null);
    }
  }
  useEffect(()=>{

    /* Load data for document selected from withing the viewer   */
    if (isSuccessRecord && viewerRecordData) {
      populateView(viewerRecordData)
    } else {
      setViewerRecord(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessRecord, viewerRecordData])

  const handleToolSelect = (recordTool: string | null): void => {
    const searchParams: URLSearchParams = new URLSearchParams(window.location.search);
    searchParams.set(Constants.RECORD_TOOL_PARAM, recordTool);
    window.history.replaceState(null, '', window.location.pathname + "?" + searchParams.toString());
    dispatch(setRecordTool(recordTool));
  }

  const icon = useCallback(():JSX.Element => {
    if (viewerState.panelCollapsed) {
      return <i className={'bi-chevron-right text-dark'}/>
    } else {
      return <i className={'bi-chevron-left text-dark'} />
    }
  }, [viewerState.panelCollapsed]);

  const subheaderIcon = useCallback((): string => {
    switch (activeRecordTool) {
      case Constants.DOCUMENT:
        return 'bi-file-text';
      case Constants.RECORDS:
        return 'bi-files'
      case Constants.CITE:
        return 'bi-quote'
      case Constants.TAGS_NOTES:
        return 'bi-bookmark'
      case Constants.DOWNLOAD:
        return 'bi-download'
      case Constants.SHARE:
        return 'bi-share'
      case Constants.REPORT_ISSUES:
        return 'bi-exclamation-circle'
    }
  }, [activeRecordTool]);

  return (
    <div className={'viewer-view view'}>
      <PageContext.Provider value={{context: Constants.VIEWER}}>
        <CardContext.Provider value={{context:Constants.DOCUMENT}}>
          {viewerState.initialRecord &&
            <ViewScrollContainer>
              <Container className={'page-container' + (viewerState.fullscreen? ' fullscreen': '')}>

              {/* Mobile back button above header */}
              {(!platform.isDesktop) && <BackSearch/>}

              {/* Header */}
              <IndustryNavbar/>

              {!viewerState.fullscreen &&
                <div className={'subheader bg-white shadow-sm mt-lg-4 pb-4'}>

                {/* Desktop back button below header */}
                {platform.isDesktop &&
                  <BackSearch/>}

                {!platform.isDesktop && (activeRecordTool !== null) &&
                  <h5 className={'mb-3 mx-2'}>
                  <i className={'bi me-1 ' + subheaderIcon()}/>
                  {Utils.toTitleCase(activeRecordTool)}
                </h5>}

                {/* mobile menu */}
                {<h5 className={'tool-title d-lg-none px-2' + (activeRecordTool === null ? ' mobile-menu' : ' bold' )}>Document Title</h5>}

                {/* Document Title */}
                <h1 className={'px-3 my-0 ellipse' +
                  (activeRecordTool === null ? ' mobile-menu' : '' )}>
                  {viewerState.viewerRecord && viewerState.viewerRecord.ti ? viewerState.viewerRecord.ti : 'Unknown Title'}
                </h1>

                {/*<OverlayTrigger
                  overlay={
                    (viewerRecord && viewerRecord.ti) && <Tooltip className={'viewer-title-tooltip' }>{viewerRecord.ti}</Tooltip>
                }>
                   <h1 className={'px-3 my-0 ellipse' +
                    (activeRecordTool === null ? ' mobile-menu' : '' )}>
                      {viewerRecord && viewerRecord.ti}
                  </h1>
                </OverlayTrigger>*/}

                {!platform.isDesktop && (activeRecordTool !== null) &&
                  <BackMenu setActiveRecordTool={(value:string)=>handleToolSelect(value)}/>}
                </div>}

                <div className={'d-lg-none'}>
                <ViewerMobile
                  fileAttributes={fileAttributes}
                />
              </div>

              <div className={'mt-3 bg-grey-medium rounded-md border d-none d-lg-block'}>
                <Row className={'m-0 flex-nowrap'}>
                  <ViewerToolbar
                    fileType={viewerState.contentView || fileAttributes?.type}
                    activeRecordTool={activeRecordTool}
                  />
                </Row>

                <Row className={'m-0 flex-nowrap'}>
                  {!viewerState.panelCollapsed &&
                    <Col className={'p-0 col-2'}>
                      <ViewerTools
                        fileType={fileAttributes?.type}
                        setActiveTool={handleToolSelect}
                      />
                  </Col>}

                  <Col className={'tools-panel-col p-0  d-flex flex-row' + (viewerState.panelCollapsed ? ' col-auto': ' col-3')}>
                    <ViewerPanel
                      setActiveTool={handleToolSelect}
                    />

                    <Button
                      className={'viewer-panel-toggle bg-light p-0 border-0 border-end rounded-0'}
                      onClick={()=>dispatch(setPanelCollapsed(!viewerState.panelCollapsed))}
                    >
                      {icon()}
                    </Button>
                  </Col>

                  <Col className={(viewerState.fullscreen ? 'fullscreen ': '') + 'mx-auto p-0 viewer-media' + (viewerState.panelCollapsed ? ' w-100': ' col-7')}>
                    {viewerState.viewerRecord && fileAttributes &&
                      <ViewerDocument
                        fileAttributes={fileAttributes}/>}
                  </Col>
                </Row>
              </div>

              </Container>
            </ViewScrollContainer>}
        </CardContext.Provider>
      </PageContext.Provider>
    </div>
  )
}

export default Viewer;
